// api/reservationApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { serverApi, handleApiResponse, handlePageResponse } from "@core/api/core"
import {
  ReservationCreateSerializer,
  ReservationExtendSerializer,
  ReservationDailycountSerializer,
  ReservationExceptionRequest,
  ReservationExceptionSerializer,
} from "@/types/reservation"
import { CommonResponse, CommonTResponse, PaginationType, initialPaginationInfo } from "@/types/common"
import { RESERVATION_STATUS } from "@/types/reservation"

const normalizeOptionalUuid = (value?: string | null): string | null => {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed || null
}

const normalizeOptionalTemplateUuid = (value?: string | null): string | null => {
  if (value === null) return null
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed || null
}

const sanitizeReservationPayload = (payload: ReservationCreateSerializer): ReservationCreateSerializer => {
  const sanitizedPayload: ReservationCreateSerializer = {
    ...payload,
  }

  if (payload.patientUuid !== undefined) {
    sanitizedPayload.patientUuid = normalizeOptionalUuid(payload.patientUuid)
  }

  if (payload.doctorUuid !== undefined) {
    sanitizedPayload.doctorUuid = normalizeOptionalUuid(payload.doctorUuid)
  }

  if (payload.smsTemplateSameDayUuid !== undefined) {
    sanitizedPayload.smsTemplateSameDayUuid = normalizeOptionalTemplateUuid(payload.smsTemplateSameDayUuid)
  }

  if (payload.smsTemplatePreviousDayUuid !== undefined) {
    sanitizedPayload.smsTemplatePreviousDayUuid = normalizeOptionalTemplateUuid(payload.smsTemplatePreviousDayUuid)
  }

  if (payload.smsTemplateCurrentDayUuid !== undefined) {
    sanitizedPayload.smsTemplateCurrentDayUuid = normalizeOptionalTemplateUuid(payload.smsTemplateCurrentDayUuid)
  }

  return sanitizedPayload
}

export const getReservationList = async (reservationDate: string): Promise<ReservationExtendSerializer[]> => {
  const response = await serverApi.get(reservationDate ? `/reservation/${reservationDate}/` : "/reservation/", { params: { includeCanceled: true } })
  return handleApiResponse<ReservationExtendSerializer[]>(response)
}

// 대시보드 예약 목록 가져오기
export const reservationFetchThunk = createAsyncThunk<ReservationExtendSerializer[], string | undefined, { rejectValue: string }>(
  `reservation/reservationFetchThunk`,
  async (reservationDate, { rejectWithValue }) => {
    try {
      return await getReservationList(reservationDate || "")
    } catch (err: any) {
      return rejectWithValue(err.message)
    }
  }
)

export const createReservation = async (payload: ReservationCreateSerializer): Promise<ReservationCreateSerializer> => {
  const requestUrl = `/reservation/`
  const sanitizedPayload = sanitizeReservationPayload(payload)
  console.log("Creating reservation with payload:", sanitizedPayload)
  const response = await serverApi.post<CommonTResponse<ReservationCreateSerializer>>(requestUrl, sanitizedPayload)
  return handleApiResponse<ReservationCreateSerializer>(response)
}

export const updateReservation = async (payload: ReservationCreateSerializer): Promise<boolean> => {
  const requestUrl = `/reservation/${payload.reservationUuid}/`
  const sanitizedPayload = sanitizeReservationPayload(payload)
  const response = await serverApi.patch<CommonResponse>(requestUrl, sanitizedPayload)
  return handleApiResponse<boolean>(response)
}

export const updateReservationWithMessage = async (payload: ReservationCreateSerializer): Promise<{ success: boolean; message: string }> => {
  const requestUrl = `/reservation/${payload.reservationUuid}/`
  const sanitizedPayload = sanitizeReservationPayload(payload)
  const response = await serverApi.patch<CommonResponse>(requestUrl, sanitizedPayload)

  return {
    success: true,
    message: String(response?.data?.message || "success"),
  }
}

export const cancelReservation = async (payload: ReservationCreateSerializer): Promise<boolean> => {
  payload = {
    ...payload,
    reservationStatus: RESERVATION_STATUS.CANCELED, // 예약을 비활성화 처리
  }
  const requestUrl = `/reservation/${payload.reservationUuid}/`
  const response = await serverApi.patch<CommonResponse>(requestUrl, payload)
  return handleApiResponse<boolean>(response)
}

export const deleteReservation = async (reservationUuid: string): Promise<boolean> => {
  const requestUrl = `/reservation/${reservationUuid}/`
  const response = await serverApi.delete<CommonResponse>(requestUrl)
  return handleApiResponse<boolean>(response)
}

export const getReservationExceptions = async (fromDate: string, toDate: string): Promise<ReservationExceptionSerializer[]> => {
  return (await getReservationExceptionsPage(fromDate, toDate, 1)).results
}

export const getReservationExceptionsPage = async (
  fromDate: string,
  toDate: string,
  page: number
): Promise<{ results: ReservationExceptionSerializer[]; pagination: PaginationType }> => {
  const response = await serverApi.get<ReservationExceptionSerializer[]>(`/reservation/exception/`, {
    params: { from_date: fromDate, to_date: toDate, page_size: 500, page },
  })
  return handlePageResponse<ReservationExceptionSerializer>(response)
}

export const createReservationException = async (payload: ReservationExceptionRequest): Promise<ReservationExceptionSerializer> => {
  const response = await serverApi.post<CommonTResponse<ReservationExceptionSerializer>>(`/reservation/exception/`, payload)
  return handleApiResponse<ReservationExceptionSerializer>(response)
}

export const updateReservationException = async (
  reservationExceptionUuid: string,
  payload: ReservationExceptionRequest
): Promise<ReservationExceptionSerializer> => {
  const response = await serverApi.patch<CommonTResponse<ReservationExceptionSerializer>>(
    `/reservation/exception/${reservationExceptionUuid}/`,
    payload
  )
  return handleApiResponse<ReservationExceptionSerializer>(response)
}

export const deleteReservationException = async (reservationExceptionUuid: string): Promise<boolean> => {
  const response = await serverApi.delete<CommonResponse>(`/reservation/exception/${reservationExceptionUuid}/`)
  return handleApiResponse<boolean>(response)
}

export const getReservationDailycount = async (reservationDate: string): Promise<ReservationDailycountSerializer[]> => {
  const requestUrl = `/reservation/dailycount/${reservationDate}/`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<ReservationDailycountSerializer[]>(response)
}

export const getReservationByDate = async (
  reservationDate: string,
  options?: { includeCanceled?: boolean }
): Promise<ReservationExtendSerializer[]> => {
  const requestUrl = `/reservation/${reservationDate}/`
  const response = await serverApi.get(requestUrl, {
    params: options?.includeCanceled ? { includeCanceled: true } : undefined,
  })
  return handleApiResponse<ReservationExtendSerializer[]>(response)
}

export const getReservationsByPatient = async (
  patientUuid: string,
  options?: { includeCanceled?: boolean; page?: number; pageSize?: number }
): Promise<{ results: ReservationExtendSerializer[]; pagination: PaginationType }> => {
  const requestUrl = `/reservation/patient/${patientUuid}/`
  const params: Record<string, string | number | boolean> = {}

  if (options?.includeCanceled) {
    params.includeCanceled = true
  }

  if (typeof options?.page === "number") {
    params.page = options.page
  }

  if (typeof options?.pageSize === "number") {
    params.page_size = options.pageSize
  }

  const response = await serverApi.get(requestUrl, {
    params: Object.keys(params).length > 0 ? params : undefined,
  })

  if (typeof options?.page === "number") {
    return handlePageResponse<ReservationExtendSerializer>(response)
  }

  return {
    results: handleApiResponse<ReservationExtendSerializer[]>(response),
    pagination: initialPaginationInfo,
  }
}

/**
 * 인쇄용 예약 목록 조회 (차트번호, pin메모 포함)
 */
export const getReservationForPrint = async (reservationDate: string): Promise<ReservationExtendSerializer[]> => {
  const requestUrl = `/reservation/print/${reservationDate}/`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<ReservationExtendSerializer[]>(response)
}

export const getReservationInfo = async (reservationUuid: string): Promise<ReservationExtendSerializer> => {
  const requestUrl = `/reservation/${reservationUuid}/`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<ReservationExtendSerializer>(response)
}

export const getNaverReservationProductKey = async (reservationUuid: string): Promise<{ reservationKey: string | null }> => {
  const response = await serverApi.get(`/reservation/${reservationUuid}/doctalk-product-key/`)
  return handleApiResponse<{ reservationKey: string | null }>(response)
}
